import { ToastrService } from 'ngx-toastr';
import { ActivatedRoute, Router } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';
import { DOCUMENT } from '@angular/common';
import { MatOptionSelectionChange } from '@angular/material/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { catchError, combineLatest, of, takeUntil, Subject, switchMap } from 'rxjs';
import { flatMap, map, without, forEach, reduce, includes, isEqual, omit, times, maxBy } from 'lodash';
import { Component, OnInit, Input, ChangeDetectorRef, OnDestroy, Renderer2, ElementRef, Inject, PLATFORM_ID } from '@angular/core';

import {
  AverageRatingType,
  AttributeValueBaseType,
  AttributeWithAttributeValueType,
  InternalProductWithFavoriteStatusType,
  InternalProductWithRatingsWithFavoriteStatusType,
} from '@sifca-monorepo/ecommerce-generator';
import { ContentJsonType, CorporateRatingType } from '@sifca-monorepo/ecommerce-generator';

import { OrdersService } from '../../../services/order/order/order.service';
import { AppService, AuthService, SeoService, SharedService } from '../../../services';
import { TranslateService } from '@ngx-translate/core';

declare const FB: any;

@Component({
  template: '',
})
export class BaguetteProductDetailComponent implements OnInit, OnDestroy {
  @Input() product: InternalProductWithFavoriteStatusType;
  private tags: HTMLMetaElement[];
  protected unsubscribeAll: Subject<any> = new Subject<any>();

  count = 0;
  items: any[];
  min: number = 1;
  activeTab = '/';
  max: number = 10;
  links: any[] = [];
  isDisabled = true;
  value: number = 1;
  posId: string = '';
  overAllAverage = 0;
  scripts: any[] = [];
  seo: ContentJsonType;
  ratingLength: number;
  ratingForm: FormGroup;
  isLastRating: boolean;
  productForm: FormGroup;
  internalProductId: any;
  authenticated: boolean;
  discountedPrice: number;
  selectedBarcodeIndex = 0;
  allSelectedIds: string[];
  isButtonDisabled: boolean;
  attributeControl: FormGroup;
  ratings: CorporateRatingType[];
  averageRating: AverageRatingType;
  selectedAttributeValues: string[] = [];
  currentUserRating: CorporateRatingType;
  productAttributes: AttributeWithAttributeValueType[];
  relatedProducts: InternalProductWithFavoriteStatusType[];
  selectedAttributes: { [groupId: string]: string | Set<string> } = {};

  get pictures() {
    return ((this.productForm?.get('barcode') as FormGroup)?.get('media') as FormGroup)?.get('pictures') as FormArray;
  }
  get publicPriceBeforeReduction() {
    return (this.productForm?.get('barcode') as FormGroup)?.get('publicPriceBeforeReduction') as FormGroup;
  }
  get discount() {
    return (this.productForm?.get('barcode') as FormGroup)?.get('discount') as FormGroup;
  }

  get filledStars(): number[] {
    const filledCount = this.count > 0 ? Math.floor(this.overAllAverage) : 0;
    return this.createArray(filledCount);
  }
  get emptyStars(): number[] {
    const emptyCount = 5 - this.filledStars.length;
    return this.createArray(emptyCount);
  }

  constructor(
    protected router: Router,
    protected el: ElementRef,
    protected renderer: Renderer2,
    protected metaTagService: Meta,
    protected appService: AppService,
    protected seoService: SeoService,
    protected titleTagService: Title,
    protected authService: AuthService,
    protected formBuilder: FormBuilder,
    protected orderService: OrdersService,
    protected activeRoute: ActivatedRoute,
    protected translate: TranslateService,
    protected toastrService: ToastrService,
    protected sharedService: SharedService,
    protected changeDetectorRef: ChangeDetectorRef,
    @Inject(DOCUMENT) protected document: Document,
    @Inject(PLATFORM_ID) protected platformId: Object,
  ) {
    this.sharedService.posId$.pipe(takeUntil(this.unsubscribeAll)).subscribe((posId) => {
      this.posId = posId;
    });
    this.authService.authenticated$.pipe(takeUntil(this.unsubscribeAll)).subscribe((authenticated: boolean) => {
      this.authenticated = authenticated;
      this.changeDetectorRef.markForCheck();
    });
    activeRoute.params.pipe(takeUntil(this.unsubscribeAll)).subscribe((params) => {
      if (this.activeRoute.snapshot.paramMap.get('id')) {
        this.appService
          .getSimpleProductWithProductAttributesAndFavoriteStatus(params['id'])
          .pipe(
            switchMap((res) => {
              if (res) {
                const catalogueCategory = maxBy(res.catalogueCategory, 'rank');
                this.appService.selectedCategories = [catalogueCategory.id];
                return combineLatest([
                  this.appService.getRelatedProducts([catalogueCategory.id]),
                  this.seoService.getSimpleProductSEOJSON(this.posId, res?.barcodes?.[0].id),
                  this.appService.getCorporateRatingsByProductWithStats(res?.id),
                  this.appService.getAverageCorporateProductRatingsByProduct(res?.id),
                  this.authenticated ? this.appService.getCurrentUserCorporateRatingsByProduct(res?.id) : of(null),
                ]);
              }
              return of(null);
            }),
          )
          .subscribe();
      } else {
        this.appService
          .getSimpleProductWithProductAttributesAndFavoriteStatusByUrlKey(params['urlKey'])
          .pipe(
            switchMap((res) => {
              if (res?.barcodes?.length) {
                const catalogueCategory = maxBy(res.catalogueCategory, 'rank');
                return combineLatest([
                  this.appService.getRelatedProducts([catalogueCategory.id]),
                  this.seoService.getSimpleProductSEOJSON(this.posId, res?.barcodes[0]?.id),
                  this.appService.getCorporateRatingsByProductWithStats(res?.id),
                  this.appService.getAverageCorporateProductRatingsByProduct(res?.id),
                  this.authenticated ? this.appService.getCurrentUserCorporateRatingsByProduct(res?.id) : of(null),
                ]);
              }
              return of(null);
            }),
          )
          .subscribe();
      }
    });
    combineLatest([this.appService.product$, this.appService.userRating$])
      .pipe(takeUntil(this.unsubscribeAll))
      .subscribe(([product, userRating]) => {
        if (product) {
          this.internalProductId = product.id;
        }
        this.currentUserRating = userRating;
        this.ratingForm = this.formBuilder.group({
          mark: [this.currentUserRating?.mark || '', Validators.required],
          comment: [this.currentUserRating?.comment || ''],
          reviewType: ['OVERALL'],
          product: [this.internalProductId, Validators.required],
        });
        const initialVal = this.ratingForm.value;
        this.ratingForm.valueChanges.pipe(takeUntil(this.unsubscribeAll)).subscribe((values) => {
          this.isDisabled = isEqual(values, initialVal);
        });
        this.changeDetectorRef.markForCheck();
      });
    this.seoService.productSeo$.pipe(takeUntil(this.unsubscribeAll)).subscribe((productSeo) => {
      if (productSeo) {
        this.seo = productSeo;
        if (this.seo?.title) {
          this.titleTagService.setTitle(this.seo?.title);
        }
        this.tags = this.metaTagService.addTags([
          ...map(this.seo?.metas, (meta) => ({
            ...(meta.name ? { name: meta.name } : {}),
            ...(meta.property ? { property: meta.property } : {}),
            content: meta.content,
          })),
          { charset: 'UTF-8' },
        ]);
          forEach(this.seo?.scripts, (seoScript) => {
            const script = this.renderer.createElement('script');
            if (seoScript.type) {
              script.type = seoScript.type;
            }
            if (seoScript.content) {
              script.text = seoScript.content;
            }
            this.scripts = [...this.scripts, script];
            this.renderer.appendChild(this.document.head, script);
          });
          forEach(this.seo?.links, (seoLink) => {
            const link = this.renderer.createElement('link');
            if (seoLink.sizes) {
              link.sizes = seoLink.sizes;
            }
            if (seoLink.href) {
              link.href = seoLink.href;
            }
            if (seoLink.rel) {
              link.rel = seoLink.rel;
            }
            this.links = [...this.links, link];
            this.renderer.appendChild(this.document.head, link);
          });
      }
    });
    this.appService.corporateRating$.subscribe((corporateRating: CorporateRatingType[]) => {
      this.ratings = corporateRating;
      if (corporateRating) {
        this.ratingLength = this.appService.corporateRatingLength;
        this.changeDetectorRef.markForCheck();
      }
    });
    this.appService.isLastRating$.pipe(takeUntil(this.unsubscribeAll)).subscribe((isLast: boolean) => {
      this.isLastRating = isLast;
    });
  }

  ngOnInit(): void {
    this.appService.searchString = '';
    if (!this.authenticated) {
      this.appService.loadCart();
      this.appService.items$.pipe(takeUntil(this.unsubscribeAll)).subscribe((items: any[]) => {
        this.items = items;
        this.changeDetectorRef.markForCheck();
      });
    }
    this.appService.product$.pipe(takeUntil(this.unsubscribeAll)).subscribe((product) => {
      if (product) {
        this.getActiveTab(product);
        this.product = product;
        this.productAttributes = product?.barcodes?.[0]?.productAttributes;
        if (!this.product.barcodes?.length) {
          this.isButtonDisabled = true;
        }
        this.internalProductId = product.id;
        this.selectedAttributeValues =
          this.product.barcodes && this.product.barcodes.length > 0
            ? map(this.product.barcodes[0].productAttributesValues?.attributesValues, (values) => values.id)
            : [];
        if (this.product.barcodes?.length) {
          this.productForm = this.formBuilder.group({
            barcode: this.formBuilder.group({
              id: [product.barcodes[0]?.id || ''],
              media: this.formBuilder.group({
                pictures: this.formBuilder.array(
                  map(product.barcodes[0].media?.pictures, (picture) => {
                    return this.formBuilder.group({ path: picture.path, baseUrl: picture.baseUrl });
                  }),
                ),
              }),
              price: [product.barcodes[0]?.publicPrice || '0'],
              publicPriceBeforeReduction: [product.barcodes[0]?.publicPriceBeforeReduction || '0'],
              name: [product.barcodes[0]?.name || ''],
              discount: this.formBuilder.group({
                amount: [product.barcodes[0]?.discount?.amount],
                discountType: [product.barcodes[0]?.discount?.discountType],
              }),
            }),
            quantity: [1],
            attributes: this.formBuilder.array(
              flatMap(product?.barcodes, (barcode) =>
                map(barcode.productAttributesValues?.attributesValues, (attributeValue) => {
                  return this.formBuilder.group({
                    label: attributeValue?.label,
                    id: [attributeValue.id, [Validators.required, Validators.min(1)]],
                    price: barcode?.publicPrice,
                  });
                }),
              ),
            ),
          });
        }
        this.changeDetectorRef.markForCheck();
      }
    });
    combineLatest([this.appService.corporateRating$, this.appService.overallAverage$])
      .pipe(takeUntil(this.unsubscribeAll))
      .subscribe(([corporateRating, average]) => {
        if (corporateRating?.length) {
          this.count = this.appService.corporateRatingLength;
          if (average) {
            this.averageRating = average;
            this.overAllAverage = Math.round(+average.overAllAverage);
          }
        }
        this.changeDetectorRef.markForCheck();
      });
    this.appService.relatedProducts$.pipe(takeUntil(this.unsubscribeAll)).subscribe((result) => {
      this.relatedProducts = result;
      this.changeDetectorRef.markForCheck();
    });
  }

  submitReview() {
    this.isDisabled = true;
    if (this.currentUserRating) {
      this.appService
        .updateCorporateRating(omit(this.ratingForm.value, 'target', 'product'), this.currentUserRating.id)
        .pipe(
          catchError(() => {
            this.translate.get('SHARED.SOMETHING_WENT_WRONG').subscribe((text: string) => {
              this.toastrService.error(text);
            });
            this.changeDetectorRef.markForCheck();
            return of(null);
          }),
          switchMap(() => {
            this.translate.get('SHARED.REVIEW_SAVED').subscribe((text: string) => {
              this.toastrService.success(text);
            });
            return combineLatest([
              this.appService.getCurrentUserCorporateRatingsByProduct(this.internalProductId),
              this.appService.getCorporateRatingsByProductWithStats(this.internalProductId),
              this.appService.getAverageCorporateProductRatingsByProduct(this.internalProductId),
            ]);
          }),
        )
        .subscribe(() => {
          this.changeDetectorRef.markForCheck();
        });
    } else {
      this.appService
        .createCorporateRating(this.posId, this.ratingForm.value)
        .pipe(
          catchError(() => {
            this.translate.get('SHARED.SOMETHING_WENT_WRONG').subscribe((text: string) => {
              this.toastrService.error(text);
            });
            this.changeDetectorRef.markForCheck();
            return of(null);
          }),
          switchMap(() => {
            this.translate.get('SHARED.REVIEW_SAVED').subscribe((text: string) => {
              this.toastrService.success(text);
            });
            return combineLatest([
              this.appService.getCurrentUserCorporateRatingsByProduct(this.internalProductId),
              this.appService.getCorporateRatingsByProductWithStats(this.internalProductId),
              this.appService.getAverageCorporateProductRatingsByProduct(this.internalProductId),
            ]);
          }),
        )
        .subscribe(() => this.changeDetectorRef.markForCheck());
    }
  }

  areAllRequiredGroupsSelected(): boolean {
    if (this.productAttributes?.length) {
      const areAllSelected = this.productAttributes
        .filter((group) => group.isRequired)
        .every((group) => {
          const selected = this.selectedAttributes?.[group?.id];
          return group.isMultipleChoice ? selected instanceof Set && selected.size > 0 : !!selected;
        });
      return areAllSelected;
    } else return true;
  }

  isChecked(groupId: string, valId: string): boolean {
    const selected = this.selectedAttributes[groupId];
    return selected instanceof Set ? selected.has(valId) : selected === valId;
  }

  clearGroup(group): void {
    if (group.isMultipleChoice) {
      (this.selectedAttributes[group.id] as Set<string>).clear();
    } else {
      this.selectedAttributes[group.id] = '';
    }
    this.allSelectedIds = this.getSelectedIds();
  }

  clearAttributes(): void {
    this.selectedAttributes = {};
    this.allSelectedIds = [];
  }

  onChangeAttributes(group: AttributeWithAttributeValueType, val: AttributeValueBaseType, checked: boolean) {
    const groupId = group.id;
    if (group.isMultipleChoice) {
      if (!this.selectedAttributes[groupId]) {
        this.selectedAttributes[groupId] = new Set<string>();
      }
      const selectedSet = this.selectedAttributes[groupId] as Set<string>;
      checked ? selectedSet.add(val.id) : selectedSet.delete(val.id);
    } else {
      this.selectedAttributes[groupId] = val.id;
    }
    this.allSelectedIds = this.getSelectedIds();
  }

  getSelectedIds(): string[] {
    return Object.values(this.selectedAttributes)
      .map((val) => (val instanceof Set ? Array.from(val) : [val]))
      .reduce((all, arr) => all.concat(arr), [] as string[])
      .filter((id) => id);
  }

  createArray(n: number): number[] {
    return times(n);
  }

  checkAttributes(change: MatOptionSelectionChange, field: string, attributeValues: any[]): void {
    if (change.isUserInput) {
      if (field !== 'multiple') {
        this.selectedAttributeValues = [
          ...reduce(
            this.selectedAttributeValues,
            (accumulator: any, current) => {
              if (includes(map(attributeValues, 'id'), current)) {
                return accumulator;
              }
              return [...accumulator, current];
            },
            [],
          ),
          change.source.value,
        ];
      } else {
        if (change?.source?.selected) {
          this.selectedAttributeValues.push(change.source.value);
        } else {
          this.selectedAttributeValues = without(this.selectedAttributeValues, change.source.value);
        }
      }
      this.changeDetectorRef.markForCheck();
    }
  }

  addToCart(product?: InternalProductWithRatingsWithFavoriteStatusType) {
    if (!this.authenticated) {
      if (this.appService.itemInCart(product ? product.barcodes[0] : this.product.barcodes[0])) {
        const value: any = {
          barcode: product ? product.barcodes[0] : this.product.barcodes[0],
          price: {
            unitGross: product ? product.barcodes[0]?.publicPrice : this.product.barcodes[0]?.publicPrice,
            gross: product ? product.barcodes[0]?.publicPrice : this.product.barcodes[0]?.publicPrice,
          },
          product: product ? product : this.product,
          ...(this.allSelectedIds?.length ? { productAttributesValues: this.allSelectedIds } : {}),
          quantity: this.productForm.get('quantity').value,
        };
        this.appService.updateLocalCart(value);
        this.translate.get('SHARED.PRODUCT_ADDED_TO_CART').subscribe((text: string) => {
          this.toastrService.success(text);
        });
      } else {
        const value: any = {
          barcode: product ? product.barcodes[0] : this.product.barcodes[0],
          price: {
            unitGross: product ? product.barcodes[0]?.publicPrice : this.product.barcodes[0]?.publicPrice,
            gross: product ? product.barcodes[0]?.publicPrice : this.product.barcodes[0]?.publicPrice,
          },
          product: product ? product : this.product,
          quantity: this.productForm.get('quantity').value,
          ...(this.allSelectedIds?.length ? { productAttributesValues: this.allSelectedIds } : {}),
        };
        this.appService.addToLocalCart(value);
        this.translate.get('SHARED.PRODUCT_ADDED_TO_CART').subscribe((text: string) => {
          this.toastrService.success(text);
        });
      }
      this.appService.loadCart();
    } else {
      this.isButtonDisabled = true;
      const formValue: any = {
        barcode: product ? product.barcodes[0]?.id : this.product.barcodes[0]?.id,
        quantity: this.productForm.get('quantity').value,
        ...(this.allSelectedIds?.length ? { productAttributesValues: this.allSelectedIds } : {}),
      };
      this.orderService
        .addProductsToCart([formValue])
        .pipe(
          catchError(() => {
            this.translate.get('SHARED.SOMETHING_WENT_WRONG').subscribe((text: string) => {
              this.toastrService.error(text);
            });
            this.changeDetectorRef.markForCheck();
            return of(null);
          }),
        )
        .subscribe((res) => {
          if (res) {
            this.isButtonDisabled = false;
            this.translate.get('SHARED.PRODUCT_ADDED_TO_CART').subscribe((text: string) => {
              this.toastrService.success(text);
            });
            this.changeDetectorRef.markForCheck();
          }
        });
    }
  }

  increment() {
    if (this.value < this.max) {
      this.value++;
    }
    this.productForm.get('quantity').patchValue(this.value);
  }

  decrement() {
    if (this.value > this.min) {
      this.value--;
    }
    this.productForm.get('quantity').patchValue(this.value);
  }

  getActiveTab(product) {
    if (product?.specs?.length) {
      this.activeTab = 'ingredients';
      return;
    }

    if (product?.longdesc) {
      this.activeTab = 'details';
      return;
    }
    this.activeTab = 'add-review';
    this.changeDetectorRef.markForCheck();
  }

  selectTab(tab: string): void {
    this.activeTab = tab;
  }

  hasSelection(groupId: string): boolean {
    const sel = this.selectedAttributes[groupId];
    if (sel instanceof Set) {
      return sel.size > 0;
    } else {
      return !!sel;
    }
  }

  onInputChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const newValue = Number(input.value);
    if (newValue >= this.min && newValue <= this.max) {
      this.value = newValue;
    } else if (newValue < this.min) {
      this.value = this.min;
    } else if (newValue > this.max) {
      this.value = this.max;
    }
    this.productForm.get('quantity').patchValue(this.value);
  }

  shareOnFacebook(text?: string) {
    const shareUrl = text + this.product?.seo?.urlKey;
    FB.init({
      appId: '278013208459559',
      autoLogAppEvents: true,
      xfbml: true,
      version: 'v22.0',
    });
    (window as any).FB.ui(
      {
        display: 'popup',
        method: 'share_open_graph',
        action_type: 'og.likes',
        action_properties: JSON.stringify({
          object: shareUrl,
        }),
      },
      function (response) {
        if (response && !response.error_message) {
          // The user successfully shared the content
          console.log('Shared successfully');
          // Perform additional actions if needed
        } else {
          // There was an error or the user canceled the share
          console.error('Share error:', response);
          // Perform additional error handling if needed
        }
      },
    );
  }

  sendOnFacebook() {
    const shareUrl = `https://baguette.diktup.cloud/p/${this.product?.seo?.urlKey}`;
    FB.ui(
      {
        method: 'send',
        link: shareUrl,
      },
      function (response) {
        if (response && !response.error_message) {
          // The user successfully shared the content
          console.log('Shared successfully');
          // Perform additional actions if needed
        } else {
          // There was an error or the user canceled the share
          console.error('Share error:', response);
          // Perform additional error handling if needed
        }
      },
    );
  }

  loginFacebook() {
    FB.login(
      function (loginResponse) {
        if (loginResponse.authResponse) {
          // User is logged in and authenticated
          console.log('User logged in:', loginResponse.authResponse);

          // Call FB.ui to trigger the share dialog
          FB.ui(
            {
              method: 'share',
              href: 'https://cocospacio.com', // Specify the URL you want to share
            },
            function (shareResponse) {
              console.log('shareResponse ', shareResponse);
              // Handle response from the share dialog
              if (shareResponse && !shareResponse.error_message) {
                // The user successfully shared the content
                console.log('Shared successfully');
                // Perform additional actions if needed
              } else {
                // There was an error or the user canceled the share
                console.error('Share error:', shareResponse);
                // Perform additional error handling if needed
              }
            },
          );
        } else {
          // User canceled the login or an error occurred
          console.log('Login error:', loginResponse.status);
        }
      },
      { scope: 'email' },
    );
  }

  ngOnDestroy() {
    this.unsubscribeAll.next(true);
    this.unsubscribeAll.complete();
    forEach(this.links, (link) => {
      return this.renderer.removeChild(this.document.head, link);
    });
    forEach(this.tags, (tag) => this.metaTagService.removeTagElement(tag));
    this.appService.product$ = null;
    this.product = null;
    this.productForm = null;
    this.ratingForm.reset();
    this.appService.corporateRating$ = null;
    this.appService.filtredProducts$ = null;
  }
}
